import type { FC } from "react"
import React from "react"
import {
  Stack,
  Flex,
  Text,
  Progress,
  Skeleton,
  Divider,
} from "@chakra-ui/react"
import invariant from "tiny-invariant"

import { ROUTES } from "common/constants/routes"
import { useRouter } from "common/hooks/useRouter"
import type { Proposal, Vote } from "query/graphql"
import {
  ProposalStatus,
  useProposalBubbleChartQuery,
  VotesSortBy,
} from "query/graphql"
import AlertMessage from "common/components/AlertMessage"
import VotingBubbleChart from "common/components/charts/VotingBubbleChart"
import {
  getTotalWeight,
  getWeightLabel,
  getWeightPercentage,
  stringToBigNumber,
} from "common/helpers/bignumber"
import { limitNumberDecimals } from "common/helpers/number"
import { getVoteStyles } from "common/helpers/voteStyle"
import { useProposal } from "proposal/providers/ProposalProvider"

type Props = {
  proposalId: string
  slug: string
  displayChart?: boolean
}

const ProposalBubbleChart: FC<Props> = ({
  proposalId,
  slug,
  displayChart = true,
}) => {
  const { isIncludePendingVotes } = useProposal()
  const { data, isLoading } = useProposalBubbleChartQuery(
    {
      input: {
        id: proposalId,
      },
      votesInput: {
        filters: {
          proposalId,
          includePendingVotes: isIncludePendingVotes,
        },
        sort: {
          sortBy: VotesSortBy.Amount,
          isDescending: true,
        },
      },
    },
    { enabled: displayChart },
  )

  if (!displayChart) {
    return null
  }

  if (isLoading) {
    return (
      <Flex as="section" direction="column" mt={4} mx={3}>
        <Stack isInline>
          <Skeleton flex={1} h={600} rounded="sm" />
        </Stack>
      </Flex>
    )
  }

  if (!data) return null

  const { proposal, votes } = data
  const { governor } = proposal

  invariant(governor, "governor is required")

  const hasTokenSupply = Number(governor.token.supply) > 0
  const hasdelegatedVotingPower = Number(governor.delegatesVotesCount) > 0

  return (
    <Stack align="stretch" direction="column" mb={0} pb={0} spacing={4}>
      {hasTokenSupply || hasdelegatedVotingPower ? (
        <BubbleChart
          proposal={proposal as Proposal}
          slug={slug}
          votes={votes.nodes as Vote[]}
        />
      ) : (
        <AlertMessage status="warning">Token has no supply</AlertMessage>
      )}
    </Stack>
  )
}

type BubbleChartProps = {
  proposal: Proposal
  votes: Vote[]
  slug: string
}
const BubbleChart: FC<BubbleChartProps> = ({ proposal, slug, votes }) => {
  const { push } = useRouter()
  const { voteStats, governor } = proposal

  invariant(governor, "governor is required")

  const weights = voteStats
    ? voteStats.map(({ votesCount }) => `${votesCount}`)
    : []
  const weight = getTotalWeight(weights)
  const weightLabel = getWeightLabel(weight, governor.token.decimals)

  const rawWeightPercentage = getWeightPercentage(
    weight,
    stringToBigNumber(governor.delegatesVotesCount),
    governor.token.decimals,
  )

  const totalWeightPercentage =
    weightLabel !== "0"
      ? rawWeightPercentage < 1 || rawWeightPercentage < 10
        ? limitNumberDecimals(rawWeightPercentage, 1)
        : limitNumberDecimals(rawWeightPercentage, 0)
      : 0

  const votingPowerLabel = `${totalWeightPercentage}%`

  const handleClickVoterBubble = (address: string) => {
    push(ROUTES.governance.delegate.profile(slug, address))
  }

  const voteStyles = getVoteStyles()

  return (
    <>
      <VotingBubbleChart
        decimals={governor.token.decimals}
        voteStats={voteStats}
        voteStyles={voteStyles}
        votes={votes}
        onClickVoterBubble={handleClickVoterBubble}
      />
      {proposal.status !== ProposalStatus.Pending ? (
        <>
          <Divider />
          <Stack p={6}>
            <Flex align="center" flex={1} justify="space-between">
              <Text textStyle="md">{votingPowerLabel}</Text>
              <Text textStyle="md">{weightLabel} votes cast</Text>
            </Flex>
            <Progress
              colorScheme="purple"
              h={1}
              textStyle="md"
              value={totalWeightPercentage}
            />
            <Text color="gray.500" textStyle="label">
              Participation rate
            </Text>
          </Stack>
        </>
      ) : null}
    </>
  )
}

export default ProposalBubbleChart
